import { type FunctionComponent } from 'react'
import { useIntl } from 'react-intl'
import { InformationIcon } from '@instacart/ids-core'
import { Tooltip } from '@retailer-platform/shared-components'
import { spacing } from '@retailer-platform/shared-components/src/tds'
import { type NavBadge } from './navBadge.types'

/**
 * Internal-only info icon rendered after a nav leaf label. On hover it reveals
 * the entry's badge(s), one per line. Renders nothing when there are no badges,
 * so external users (and non-badged entries) see an unchanged row.
 *
 * The trigger is a non-interactive <span>, so it is safe to nest inside the
 * NavItem anchor.
 */
export const NavBadgeIndicator: FunctionComponent<{ badges: NavBadge[] }> = ({ badges }) => {
  const intl = useIntl()

  if (badges.length === 0) return null

  const labels = badges.map(badge => intl.formatMessage({ id: badge.labelId }, badge.values))

  return (
    <Tooltip
      target={
        <span
          role="img"
          aria-label={labels.join(', ')}
          style={{ display: 'inline-flex', alignItems: 'center', marginLeft: spacing.s4 }}
        >
          <InformationIcon size={16} color="systemGrayscale50" />
        </span>
      }
    >
      {labels.map((label, index) => (
        <div key={badges[index].id}>{label}</div>
      ))}
    </Tooltip>
  )
}
